import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner, Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getAccountingSettings,
    updateAccountingSettings,
    updateBusinessIdentity,
    updateTestMode,
} from '../../../services/AccountingService';
import { isSuperAdmin } from '../../../services/AuthService';
import SettingsPage from '../Common/SettingsPage';

const SOURCES = ['shopify', 'website', 'manual'];
const MODES = ['as_sent', 'catalog'];
const RATES = ['0', '9', '21'];
const TRIGGERS = ['paid_or_delivered', 'paid', 'delivered'];
const IDENTITY_FIELDS = [
    'business_name', 'street_address', 'postcode_city', 'email', 'phone', 'kvk_number', 'btw_nummer', 'iban', 'country_code',
];

const settingsSchema = (t) => Yup.object({
    shipping_btw_rate: Yup.string().oneOf(RATES, t('accounting.errors.shippingRate')).required(),
    invoice_issue_trigger: Yup.string().oneOf(TRIGGERS).required(),
});

const passwordSchema = (t) => Yup.object({
    password: Yup.string().required(t('accounting.testMode.passwordRequired')),
});

const identitySchema = (t) => Yup.object({
    email: Yup.string().email(t('accounting.errors.email')),
    iban: Yup.string().matches(/^$|^[A-Za-z]{2}\d{2}[A-Za-z0-9 ]{10,30}$/, t('accounting.errors.iban')),
    country_code: Yup.string().matches(/^[A-Za-z]{2}$/, t('accounting.errors.country')).required(t('accounting.errors.country')),
});

function apiMessage(err, t, fallbackKey) {
    const key = err.response?.data?.error_key;
    return key ? t(key) : (err.response?.data?.error || t(fallbackKey));
}

export default function AccountingSettings() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [confirmTarget, setConfirmTarget] = useState(null);   // true/false = the mode being switched to
    const [missing, setMissing] = useState([]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getAccountingSettings();
            setData(res.data);
        } catch {
            toast.error(t('accounting.errors.load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    if (loading || !data) {
        return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }

    const { settings, readiness, business } = data;
    const checklist = readiness.items;

    const settingsInitial = {
        order_pricing_mode: { ...settings.order_pricing_mode },
        shipping_btw_rate: settings.shipping_btw_rate,
        shipping_btw_rate_confirmed: settings.shipping_btw_rate_confirmed,
        invoice_issue_trigger: settings.invoice_issue_trigger,
    };
    const identityInitial = IDENTITY_FIELDS.reduce((acc, f) => ({ ...acc, [f]: business[f] || '' }), {});

    const saveSettings = async (values, { setSubmitting }) => {
        try {
            const res = await updateAccountingSettings({ ...values, shipping_btw_rate: String(values.shipping_btw_rate) });
            setData(res.data);
            toast.success(t('accounting.saved'));
        } catch (err) {
            toast.error(apiMessage(err, t, 'accounting.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    const closeConfirm = () => { setConfirmTarget(null); setMissing([]); };

    const confirmTestMode = async (values, { setSubmitting, setFieldError }) => {
        try {
            const res = await updateTestMode(confirmTarget, values.password);
            setData(res.data);
            toast.success(t(confirmTarget ? 'accounting.testMode.nowTest' : 'accounting.testMode.nowLive'));
            closeConfirm();
        } catch (err) {
            if (err.response?.status === 422) {
                setMissing(err.response.data.missing || []);
            } else if (err.response?.data?.error_key === 'accounting.testMode.errors.wrongPassword') {
                setFieldError('password', t('accounting.testMode.errors.wrongPassword'));
            } else {
                toast.error(apiMessage(err, t, 'accounting.errors.save'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const saveIdentity = async (values, { setSubmitting }) => {
        try {
            const res = await updateBusinessIdentity(values);
            setData(res.data);
            toast.success(t('accounting.saved'));
        } catch (err) {
            toast.error(apiMessage(err, t, 'accounting.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <SettingsPage className="accounting-settings" title={t('nav.invoicingAccounting')}
            banners={(
                <Alert variant={settings.test_mode ? 'warning' : 'success'} className="accounting-banner" data-testid="test-mode-banner">
                    <strong>{settings.test_mode ? t('accounting.testMode.bannerOn') : t('accounting.testMode.bannerLive')}</strong>
                    <div>{settings.test_mode ? t('accounting.testMode.bannerOnText') : t('accounting.testMode.bannerLiveText')}</div>
                </Alert>
            )}>
            <Card className="mb-4">
                <Card.Header><Card.Title>{t('accounting.testMode.title')}</Card.Title></Card.Header>
                <Card.Body>
                    <p className="text-muted small">{t('accounting.testMode.help')}</p>
                    <Form.Check
                        type="switch"
                        id="test_mode"
                        label={t('accounting.testMode.label')}
                        checked={settings.test_mode}
                        disabled={!isSuperAdmin()}
                        onChange={(e) => setConfirmTarget(e.target.checked)}
                    />
                    {!isSuperAdmin() && <Form.Text className="text-muted">{t('accounting.testMode.superAdminOnly')}</Form.Text>}
                </Card.Body>
            </Card>

            <Modal show={confirmTarget !== null} onHide={closeConfirm} centered>
                <Formik initialValues={{ password: '' }} validationSchema={passwordSchema(t)} onSubmit={confirmTestMode}>
                    {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton>
                                <Modal.Title>{confirmTarget ? t('accounting.testMode.confirmTitleTest') : t('accounting.testMode.confirmTitleLive')}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p>{confirmTarget ? t('accounting.testMode.confirmTextTest') : t('accounting.testMode.confirmTextLive')}</p>
                                {missing.length > 0 && (
                                    <Alert variant="danger" data-testid="test-mode-missing">
                                        <div>{t('accounting.testMode.errors.notReady')}</div>
                                        <ul className="mb-0">
                                            {missing.map((key) => <li key={key}>{t(`accounting.readiness.items.${key}`)}</li>)}
                                        </ul>
                                    </Alert>
                                )}
                                <Form.Label htmlFor="test-mode-password">{t('accounting.testMode.passwordLabel')}</Form.Label>
                                <Form.Control
                                    id="test-mode-password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={values.password}
                                    onChange={handleChange}
                                    isInvalid={!!errors.password && (touched.password || isSubmitting || !!errors.password)}
                                />
                                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline-secondary" onClick={closeConfirm}>{t('common.cancel')}</Button>
                                <Button type="submit" variant={confirmTarget ? 'warning' : 'success'} disabled={isSubmitting}>
                                    {confirmTarget ? t('accounting.testMode.confirmButtonTest') : t('accounting.testMode.confirmButtonLive')}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>

            <Card className="mb-4">
                <Card.Header><Card.Title>{t('accounting.settingsTitle')}</Card.Title></Card.Header>
                <Card.Body>
                    <Formik initialValues={settingsInitial} validationSchema={settingsSchema(t)} onSubmit={saveSettings}>
                        {({ values, errors, handleChange, setFieldValue, handleSubmit, isSubmitting }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <h6>{t('accounting.pricing.title')}</h6>
                                <p className="text-muted small">{t('accounting.pricing.help')}</p>
                                <Row className="mb-3">
                                    {SOURCES.map((src) => (
                                        <Col md={4} key={src} className="mb-2">
                                            <Form.Label htmlFor={`pricing-${src}`}>{t(`accounting.sources.${src}`)}</Form.Label>
                                            <Form.Select
                                                id={`pricing-${src}`}
                                                value={values.order_pricing_mode[src]}
                                                onChange={(e) => setFieldValue(`order_pricing_mode.${src}`, e.target.value)}
                                            >
                                                {MODES.map((m) => <option key={m} value={m}>{t(`accounting.modes.${m}`)}</option>)}
                                            </Form.Select>
                                        </Col>
                                    ))}
                                </Row>

                                <h6>{t('accounting.shipping.title')}</h6>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Label htmlFor="shipping_btw_rate">{t('accounting.shipping.rate')}</Form.Label>
                                        <Form.Select id="shipping_btw_rate" name="shipping_btw_rate" value={values.shipping_btw_rate} onChange={handleChange} isInvalid={!!errors.shipping_btw_rate}>
                                            {RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                                        </Form.Select>
                                        <Form.Text className="text-muted">{t('accounting.shipping.hint')}</Form.Text>
                                    </Col>
                                    <Col md={8} className="d-flex align-items-end">
                                        <Form.Check
                                            type="switch"
                                            id="shipping_btw_rate_confirmed"
                                            label={t('accounting.shipping.confirmed')}
                                            checked={values.shipping_btw_rate_confirmed}
                                            onChange={(e) => setFieldValue('shipping_btw_rate_confirmed', e.target.checked)}
                                        />
                                    </Col>
                                </Row>

                                <h6>{t('accounting.issuing.title')}</h6>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Label htmlFor="invoice_issue_trigger">{t('accounting.issuing.trigger')}</Form.Label>
                                        <Form.Select id="invoice_issue_trigger" name="invoice_issue_trigger" value={values.invoice_issue_trigger} onChange={handleChange}>
                                            {TRIGGERS.map((tr) => <option key={tr} value={tr}>{t(`accounting.triggers.${tr}`)}</option>)}
                                        </Form.Select>
                                    </Col>
                                </Row>
                                <Button type="submit" disabled={isSubmitting}>{t('common.save')}</Button>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header><Card.Title>{t('accounting.business.title')}</Card.Title></Card.Header>
                <Card.Body>
                    <p className="text-muted small">{t('accounting.business.help')}</p>
                    <Formik initialValues={identityInitial} validationSchema={identitySchema(t)} onSubmit={saveIdentity}>
                        {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Row>
                                    {IDENTITY_FIELDS.map((field) => (
                                        <Col md={field === 'country_code' ? 2 : 4} key={field} className="mb-3">
                                            <Form.Label htmlFor={`biz-${field}`}>{t(`accounting.business.fields.${field}`)}</Form.Label>
                                            <Form.Control
                                                id={`biz-${field}`}
                                                name={field}
                                                value={values[field]}
                                                onChange={handleChange}
                                                maxLength={field === 'country_code' ? 2 : undefined}
                                                isInvalid={!!errors[field]}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors[field]}</Form.Control.Feedback>
                                        </Col>
                                    ))}
                                </Row>
                                <Button type="submit" disabled={isSubmitting}>{t('common.save')}</Button>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header><Card.Title>{t('accounting.readiness.title')}</Card.Title></Card.Header>
                <Card.Body>
                    <p className="text-muted small">{t('accounting.readiness.help')}</p>
                    <ul className="accounting-readiness-list list-unstyled mb-0">
                        {checklist.map((item) => (
                            <li key={item.key} className={`accounting-readiness-item ${item.ok ? 'is-ok' : (item.blocking ? 'is-blocking' : 'is-warning')}`}>
                                <i className={`fa-solid ${item.ok ? 'fa-circle-check' : (item.blocking ? 'fa-circle-xmark' : 'fa-triangle-exclamation')} me-2`} />
                                {t(`accounting.readiness.items.${item.key}`)}
                                {!item.ok && (
                                    <span className="text-muted small ms-2">
                                        {item.blocking ? t('accounting.readiness.blocking') : t('accounting.readiness.warning')}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3">
                        <strong>{readiness.ready ? t('accounting.readiness.ready') : t('accounting.readiness.notReady')}</strong>
                    </div>
                </Card.Body>
            </Card>
        </SettingsPage>
    );
}
